import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip, alpha } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import {
  fmtScore,
  scoreColor,
  toneColor,
  localizeCopy,
  localizableCopy,
} from './helpers';

// ── Stat Tile ──
const Tile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden',
    borderRadius: 2, border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff', p: '14px 16px', transition: 'all 0.2s',
    '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
  }}>
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: 2, flexShrink: 0, background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 3px 8px ${alpha(color, 0.25)}` }}>
        <Icon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 900, color, lineHeight: 1.2, fontFamily: 'monospace' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.4 }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);
Tile.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sub: PropTypes.node,
  color: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
};

// === Score Bar ===
const ScoreBar = ({ score, maxScore = 100, height = 6 }) => {
  const pct = Math.min(100, Math.max(0, (score / maxScore) * 100));
  const c = scoreColor(score);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
      <Box sx={{ flex: 1, height, bgcolor: alpha(c, 0.1), borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: c, borderRadius: 1, transition: 'width 0.5s ease' }} />
      </Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: c, minWidth: 30, textAlign: 'right' }}>
        {fmtScore(score)}
      </Typography>
    </Box>
  );
};
ScoreBar.propTypes = {
  score: PropTypes.number,
  maxScore: PropTypes.number,
  height: PropTypes.number,
};


const InsightRow = ({ item, tone = 'positive', lang, isLast = false }) => {
  const mainColor = toneColor(item?.tone, tone === 'positive' ? '#10b981' : '#f59e0b');
  const textColor = tone === 'positive' ? '#334155' : '#92400e';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.8,
        py: 0.9,
        borderBottom: isLast ? 'none' : '1px dashed #e2e8f0',
      }}
    >
      <Box sx={{ width: 3, borderRadius: 999, alignSelf: 'stretch', bgcolor: alpha(mainColor, 0.9), minHeight: 34 }} />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.8, flex: 1, minWidth: 0 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {localizeCopy(item.title, lang) || '—'}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: textColor, lineHeight: 1.45, mt: 0.18 }}>
            {localizeCopy(item.detail, lang) || '—'}
          </Typography>
        </Box>
        {item.evidence ? (
          <Chip
            size="small"
            label={localizeCopy(item.evidence, lang) || item.evidence}
            sx={{
              height: 22,
              flexShrink: 0,
              borderRadius: 999,
              bgcolor: alpha(mainColor, 0.1),
              color: mainColor,
              border: `1px solid ${alpha(mainColor, 0.18)}`,
              fontWeight: 800,
              fontSize: '0.62rem',
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
};
InsightRow.propTypes = {
  item: PropTypes.shape({
    tone: PropTypes.string,
    title: localizableCopy,
    detail: localizableCopy,
    evidence: localizableCopy,
  }).isRequired,
  tone: PropTypes.string,
  lang: PropTypes.string,
  isLast: PropTypes.bool,
};


// === Empty State ===
const EmptyState = ({ Icon, title, subtitle }) => (
  <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {Icon && <Icon sx={{ fontSize: 24, color: '#cbd5e1' }} />}
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#94a3b8', lineHeight: 1.4 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.3, maxWidth: 400 }}>{subtitle}</Typography>}
    </Box>
  </Box>
);
EmptyState.propTypes = {
  Icon: PropTypes.elementType,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
};

// === Sort Icon ===
const SortIcon = ({ col, sortCol, sortDir }) => {
  if (sortCol !== col) return null;
  return sortDir === 'desc'
    ? <ArrowDownward sx={{ fontSize: 12, ml: 0.3 }} />
    : <ArrowUpward sx={{ fontSize: 12, ml: 0.3 }} />;
};
SortIcon.propTypes = {
  col: PropTypes.string.isRequired,
  sortCol: PropTypes.string,
  sortDir: PropTypes.oneOf(['asc', 'desc']),
};

export { Tile, ScoreBar, InsightRow, EmptyState, SortIcon };
